import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";

import CompactViewForm from "./ViewDialog/CompactViewForm";

import columnConfig from "./helpers/columnConfig";
import { ROOT_GROUP_ID, TABLE_HEADERS } from "./constants";
import { useIntl } from "react-intl";
import cloneDeep from "lodash/cloneDeep";

const COLUMN_STORAGE_KEY = "incomingOrders.visibleColumns";

const PERIOD_PRESETS = {
  ALL: "all",
  LAST_7_DAYS: "last7",
  LAST_30_DAYS: "last30",
  CUSTOM: "custom",
};

const emptyFilters = () => ({
  productTypes: [],
  supplierOrderStatusIds: [],
  orderDateFrom: "",
  orderDateTo: "",
  orderStatus: null,
  periodPreset: PERIOD_PRESETS.ALL,
});

const normalizePeriodPreset = (filters = {}) => {
  const preset = filters.periodPreset;

  if (Object.values(PERIOD_PRESETS).includes(preset)) {
    return preset;
  }

  return filters.orderDateFrom || filters.orderDateTo
    ? PERIOD_PRESETS.CUSTOM
    : PERIOD_PRESETS.ALL;
};

const normalizeFilters = (filters = {}) => ({
  ...emptyFilters(),
  ...filters,
  productTypes: Array.isArray(filters.productTypes) ? filters.productTypes : [],
  supplierOrderStatusIds: Array.isArray(filters.supplierOrderStatusIds) ? filters.supplierOrderStatusIds : [],
  periodPreset: normalizePeriodPreset(filters),
});

const createEmptyViewDraft = (filters = {}) => ({
  id: 0,  
  name: "",  
  filters: normalizeFilters(filters),
  settings: {
    
    columns: {
      saveCurrent: true,
    },
  },
});

const isDateRangeInvalid = (filters = {}) => {
  const normalized = normalizeFilters(filters);
  return Boolean(
    normalized.orderDateFrom &&
    normalized.orderDateTo &&
    normalized.orderDateFrom > normalized.orderDateTo
  );
};

const getDefaultColumnVisibility = () => Object.fromEntries(
  TABLE_HEADERS.map((columnType) => [columnType, true])
);

const readStoredColumnVisibility = () => {
  if (typeof window === "undefined") return getDefaultColumnVisibility();

  try {
    const stored = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    if (stored) {
      return {
        ...getDefaultColumnVisibility(),
        ...JSON.parse(stored),
      };
    }
  } catch (err) {
    console.warn("Failed to read incoming order column settings", err);
  }

  return getDefaultColumnVisibility();
};

const normalizeView = (view) => {
  if (!view) return null;

  let parsedSettings = {};

  try {
    parsedSettings =
      typeof view.settings === "string"
        ? JSON.parse(view.settings)
        : view.settings ?? {};
  } catch {
    parsedSettings = {};
  }

  return {
    id: view.id ?? 0,
    groupId: view.groupId ?? null,
    name: view.name ?? "",

    filters: {
      productTypes: [],
      supplierOrderStatusIds: [],
      orderDateFrom: "",
      orderDateTo: "",
      orderStatus: null,
      periodPreset: "all",
      dateFrom: null,
      dateTo: null,
      deliveryDateFrom: null,
      deliveryDateTo: null,
      orderingCompanyIds: [],
      ...(parsedSettings.filters ?? {}),
    },

    settings: {
      columns: {
        saveCurrent: true,
      },
      ...(parsedSettings ?? {}),
    },
  };
};


const IncomingOrderViewDialog = ({
  open,
  groups,  
  statuses = [],
  currentFilters,
  onClose,
  onCreateView,
  onCreateGroupClick,
  onGetColumnVisibility,
  onToggleColumn,
  sourceView,
  onDeleteView
}) => {
  const intl = useIntl();
  const [viewDraft, setViewDraft] = useState(() => createEmptyViewDraft());
  const [groupTarget, setGroupTarget] = useState(ROOT_GROUP_ID);
  const [visibleColumns, setVisibleColumns] = useState(() => readStoredColumnVisibility());

  useEffect(() => {
    if (!open) return;

    const initialView = sourceView
      ? normalizeView(cloneDeep(sourceView))
      : createEmptyViewDraft(normalizeFilters(currentFilters));

    

    setGroupTarget(sourceView?.groupId ?? ROOT_GROUP_ID);

    setViewDraft({
      ...initialView,
      id: sourceView?.id ?? 0,
      filters: normalizeFilters(initialView.filters),
    });

    setVisibleColumns(onGetColumnVisibility?.() ?? readStoredColumnVisibility());
  }, [open, sourceView, currentFilters, onGetColumnVisibility]);

  const canSave = Boolean(viewDraft.name.trim()) && !isDateRangeInvalid(viewDraft.filters);

  const handleColumnToggle = (columnType) => {
    if (columnConfig[columnType]?.required) return;

    setVisibleColumns((prev) => ({
      ...prev,
      [columnType]: prev[columnType] === false,
    }));

    onToggleColumn?.(columnType);
  };

  const handleSave = () => {

    if (!canSave) return;

    

    onCreateView({
      ...viewDraft,
      groupId: groupTarget === ROOT_GROUP_ID ? null : groupTarget,      
    });

  };

  const isEditing = Boolean(sourceView?.id);

  const handleDelete = () => {
    if (!isEditing) return;

    onDeleteView?.(sourceView.id);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 64px)",
        },
      }}
    >
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.25 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "12px",
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <VisibilityRoundedIcon fontSize="small" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>
              {intl.formatMessage({
                id: "incomingOrders.views.saveView"
              })}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 12.5, mt: 0.35 }}>
              {intl.formatMessage({
                id: "incomingOrders.views.saveViewDescription"
              })}
            </Typography>
          </Box>

          <IconButton size="small" onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.25, pb: 1.5, minHeight: 0, overflowY: "auto" }}>
        <CompactViewForm
          draft={viewDraft}
          onChange={setViewDraft}
          groups={groups}
          groupTarget={groupTarget}
          onGroupTargetChange={setGroupTarget}          
          statuses={statuses}
          onCreateGroupClick={onCreateGroupClick}
          visibleColumns={visibleColumns}
          onToggleColumn={handleColumnToggle}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          flexShrink: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {isEditing && (
            <Button
              color="error"
              onClick={handleDelete}
              sx={{ textTransform: "none" }}
            >
              {intl.formatMessage({
                id: "common.delete"
              })}
            </Button>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            {intl.formatMessage({
              id: "common.cancel",
            })}
          </Button>

          <Button
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{ textTransform: "none" }}
          >
            {intl.formatMessage({
              id: isEditing ? "btn.save" : "incomingOrders.views.saveView"
            })}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default IncomingOrderViewDialog;
